import { useState } from "react";
import moment from "moment";
import clsx from "clsx";
import { Chip, EngineChips } from "./Chip";
import { IncidentStatusChip } from "./IncidentStatusChip";
import { StateChipBare } from "./StateChip";
import {
  GROUP_TD_CLASS,
  sectionAnchorId,
  TABLE_CARD_CLASS,
  TABLE_CLASS,
  TABLE_HEADER_Z,
} from "./FleetTable";
import { useStickyCollapsed } from "../hooks/useStickyCollapse";
import { fmtActor, fmtDate } from "../format";
import type { HiddenOnboardingRows } from "../hidden-rows";
import {
  currentPipelineStep,
  isLiveJoined,
  OnboardingMergedRow,
} from "../onboarding-merge";
import type { OnboardingHiddenRow } from "../types";
import type {
  ProvisioningUser,
  StepStatus,
} from "../../../onboarding-tracking/types";

/** Same sticky geometry, table model and card as the fleet table below it —
 *  one number, one shape, so the two read as one surface (census.html). The
 *  `border-separate` model is required for sticky cells; see FleetTable. */
const TH_CLASS =
  // Same z as the fleet table's header, and for the same reason — see
  // `TABLE_HEADER_Z`: anything above `z-[1]` here outranks the side drawer and
  // leaves the head lit while the page dims behind it.
  `sticky top-0 ${TABLE_HEADER_Z} h-[56px] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-[#F4F4F6] whitespace-nowrap shadow-[0_1px_0_#E9E9EB]`;
const TD_CLASS = "px-4 py-3.5 border-b border-b-[#E9E9EB] text-sm align-top";

function fromNow(iso: string | null | undefined): string {
  return iso ? moment(iso).fromNow() : "—";
}

/**
 * Whose judgement a hidden row carries, on the Unhide button.
 *
 * The point of moving this list off one browser (2026-08-17) is that hiding a
 * row now has an author, so the tooltip names them — the optimistic row that
 * exists for the moment before the server answers carries no actor yet, and
 * says the date alone rather than guessing.
 */
function hiddenDetail(info: OnboardingHiddenRow | null): string {
  if (!info) return "Show this row again";
  const who = fmtActor(info.hiddenBy);
  const when = fmtDate(info.hiddenAt);
  return who
    ? `Hidden by ${who} on ${when} — show it again`
    : `Hidden on ${when} — show it again`;
}

/** Self-Serve / Concierge / Legacy — the onboarding page's badge, in the
 *  health dashboard's chip geometry. */
export function ModeBadge({
  setupMode,
}: {
  setupMode: ProvisioningUser["setupMode"];
}) {
  if (setupMode === "self_serve") return <Chip tone="blue">Self-Serve</Chip>;
  if (setupMode === "concierge") return <Chip tone="green">Concierge</Chip>;
  return <Chip tone="neutral">Legacy</Chip>;
}

/**
 * The one cell that replaced three columns: the step the pipeline is waiting
 * on, with a small tag ONLY when the status is not the waiting-on-a-human
 * norm — error and in-progress are the exceptions worth ink (the old Status
 * column was "Awaiting Manual" 99% of the time, and the old Progress bar
 * said nothing "Step k of n" doesn't).
 */
export function PipelineStepSummary({
  user,
  awaitingFirstSpend,
}: {
  user: ProvisioningUser;
  awaitingFirstSpend?: boolean;
}) {
  if (user.overallStatus === "COMPLETED") {
    return (
      <div>
        <div className="text-sm font-medium text-content-primary">
          All steps done
        </div>
        {awaitingFirstSpend && (
          <div className="text-xs text-content-secondary mt-0.5">
            awaiting first spend — campaigns have never served
          </div>
        )}
      </div>
    );
  }

  const current = currentPipelineStep(user);
  if (!current) {
    return <div className="text-sm text-content-secondary">—</div>;
  }

  const tag =
    current.step.status === "FAILED" ? (
      <Chip tone="red">error</Chip>
    ) : current.step.status === "IN_PROGRESS" ? (
      <Chip tone="blue">in progress</Chip>
    ) : user.overallStatus === "NOT_STARTED" ? (
      <Chip tone="neutral">not started</Chip>
    ) : null;

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-content-primary">
          {current.step.label}
        </span>
        {tag}
      </div>
      <div className="text-xs text-content-secondary mt-0.5">
        Step {current.step.order} of {current.total}
      </div>
    </div>
  );
}

/**
 * One segment per step, in the portal's reserved palette (§3.2): green done,
 * blue running, orange waiting on a human, red broken, grey not reached.
 *
 * Skipped counts as done — a GA4-only hotel legitimately never runs the GTM
 * steps — but reads paler, so a full-looking bar is still distinguishable
 * from one where the pipeline actually performed every step.
 */
const STEP_SEGMENT: Record<StepStatus, { className: string; label: string }> = {
  COMPLETED: { className: "bg-success-dark", label: "done" },
  SKIPPED: { className: "bg-success-light", label: "skipped" },
  IN_PROGRESS: { className: "bg-info-dark", label: "in progress" },
  AWAITING_MANUAL: { className: "bg-warning-dark", label: "awaiting manual" },
  FAILED: { className: "bg-danger-dark", label: "failed" },
  PENDING: { className: "bg-[#E9E9EB]", label: "not reached" },
};

/**
 * The pipeline's shape at a glance: how far along, and — because each segment
 * carries its own status color rather than filling one bar to a percentage —
 * where the failure or the wait actually sits among the steps. Per-segment
 * tooltips name the step, so the colors need no legend.
 */
export function StepsProgress({ user }: { user: ProvisioningUser | null }) {
  // A legacy tracking-only row has no pipeline to draw (the Current step
  // column says so in words).
  if (!user || user.steps.length === 0) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const total = user.steps.length;
  const done = user.steps.filter(
    (s) => s.status === "COMPLETED" || s.status === "SKIPPED",
  ).length;
  return (
    <div className="w-[150px]">
      <div
        className="flex gap-0.5"
        role="img"
        aria-label={`${done} of ${total} steps done`}
      >
        {user.steps.map((step) => (
          <span
            key={step.step}
            data-tip={`${step.order}. ${step.label} — ${STEP_SEGMENT[step.status].label}`}
            className={clsx(
              "h-1.5 flex-1 rounded-[1px]",
              STEP_SEGMENT[step.status].className,
            )}
          />
        ))}
      </div>
      <div className="text-xs text-content-secondary mt-1">
        {done} of {total} done
      </div>
    </div>
  );
}

/**
 * The "Still onboarding" section of the merged fleet table — census.html's
 * separation, as a live surface: hotels that have not gone live sit outside
 * the health states, carrying their pipeline's current step instead of
 * metric columns. Rows with a pipeline open the pipeline drawer; legacy
 * tracking-only rows open the hotel page.
 */
export function OnboardingSection({
  rows,
  hiddenRows: hiddenList,
  onOpenPipeline,
  onOpenHotel,
}: {
  rows: OnboardingMergedRow[];
  hiddenRows: HiddenOnboardingRows;
  onOpenPipeline: (userId: string) => void;
  onOpenHotel: (customerId: string) => void;
}) {
  const [showHidden, setShowHidden] = useState(false);
  // Sticky, like the fleet table's groups: this section is irrelevant to
  // anyone not working onboarding, and collapsing it should stay collapsed.
  const [collapsed, toggleCollapsed] = useStickyCollapsed("onboarding-group");

  const hiddenRows = rows.filter(hiddenList.isHidden);
  const visibleRows = showHidden
    ? rows
    : rows.filter((row) => !hiddenList.isHidden(row));

  if (rows.length === 0) return null;

  return (
    <div className={TABLE_CARD_CLASS}>
      <table className={TABLE_CLASS}>
        <thead>
          <tr>
            <th className={clsx(TH_CLASS, "rounded-tl-lg")}>Hotel</th>
            {/* "PMS" is what the provisioning pipeline's own field is called.
                Nothing else in Tracking Health uses that word — the module
                says "booking engine" everywhere, and a column of engine names
                does not need the acronym to be understood. */}
            <th className={TH_CLASS}>Booking engine</th>
            <th className={TH_CLASS}>Mode</th>
            <th className={TH_CLASS}>Current step</th>
            <th className={TH_CLASS}>Progress</th>
            <th className={TH_CLASS}>Started</th>
            <th className={TH_CLASS}>Updated</th>
            <th className={clsx(TH_CLASS, "rounded-tr-lg")} />
          </tr>
        </thead>
        <tbody>
          {/* The group header row, the fleet table's shape: it IS the
              section's title, its count and its collapse control, and it
              pins under the column header while the group scrolls. */}
          <tr
            id={sectionAnchorId("onboarding")}
            className="scroll-mt-2 cursor-pointer select-none"
            onClick={toggleCollapsed}
            data-tip={collapsed ? "Expand this group" : "Collapse this group"}
          >
            <td colSpan={8} className={clsx(GROUP_TD_CLASS, "px-4")}>
              <span className="flex items-center gap-1">
                <span
                  className={clsx(
                    "inline-block w-3 text-[10px] transition-transform",
                    !collapsed && "rotate-90",
                  )}
                >
                  ▶
                </span>
                <span className="hover:text-content-primary transition-colors">
                  Still onboarding ({rows.length - hiddenRows.length})
                </span>
                <span className="normal-case font-medium tracking-normal">
                  {" "}
                  · onboarding in progress — outside the health states
                  {collapsed && " · collapsed"}
                </span>
                {!collapsed && hiddenRows.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHidden((v) => !v);
                    }}
                    className="ml-auto normal-case tracking-normal text-xs font-medium text-brand-primary hover:text-brand-primary-hover"
                  >
                    {showHidden
                      ? "Hide hidden rows"
                      : `Show hidden (${hiddenRows.length})`}
                  </button>
                )}
              </span>
            </td>
          </tr>

          {!collapsed && visibleRows.length === 0 && (
            <tr>
              <td
                colSpan={8}
                className="px-4 py-8 text-center text-sm text-content-secondary"
              >
                Every onboarding row is hidden.
              </td>
            </tr>
          )}

          {!collapsed &&
            visibleRows.map((row) => {
                const hiddenInfo = hiddenList.hiddenInfo(row);
                const isHidden = hiddenInfo !== null;
                return (
                  <tr
                    key={row.key}
                    className={clsx(
                      "cursor-pointer hover:bg-gray-50 transition-colors group",
                      isHidden && "opacity-50",
                    )}
                    onClick={() =>
                      row.user
                        ? onOpenPipeline(row.user.userId)
                        : row.hotel && onOpenHotel(row.hotel.customerId)
                    }
                  >
                    <td className={TD_CLASS}>
                      <div className="font-medium text-content-primary">
                        {row.user?.hotelName ??
                          row.hotel?.hotelName ??
                          "(no hotel name)"}
                      </div>
                      <div className="text-xs text-content-secondary">
                        {row.user?.userEmail ?? row.hotel?.ownerEmail ?? "—"}
                      </div>
                      {/* Identity, not progress: the state chip marks a row
                          that ALSO sits in the live table (an unfinished
                          pipeline on a serving hotel), and an incident that
                          reached an OUTCOME is worth saying here. Both used to
                          ride the Tracking column the progress bar replaced.
                          Since 2026-08-17 an active incident chips nothing —
                          a phase of our own fix plan is neither identity nor
                          this section's subject. */}
                      {row.hotel &&
                        (isLiveJoined(row) || row.hotel.openIncident) && (
                          <span className="inline-flex items-center gap-1.5 flex-wrap mt-1.5">
                            {isLiveJoined(row) && (
                              <StateChipBare state={row.hotel.status.reported} />
                            )}
                            {row.hotel.openIncident && (
                              <IncidentStatusChip
                                incident={row.hotel.openIncident}
                              />
                            )}
                          </span>
                        )}
                    </td>
                    <td className={TD_CLASS}>
                      {row.user ? (
                        <span className="inline-flex items-center gap-1 flex-wrap">
                          <Chip tone="neutral">{row.user.pmsType ?? "?"}</Chip>
                          {row.user.isGa4Only && <Chip tone="purple">GA4</Chip>}
                        </span>
                      ) : row.hotel ? (
                        <EngineChips
                          engines={row.hotel.engines}
                          trackingMode={row.hotel.trackingMode}
                        />
                      ) : null}
                    </td>
                    <td className={TD_CLASS}>
                      {row.user ? (
                        <ModeBadge setupMode={row.user.setupMode} />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className={TD_CLASS}>
                      {row.user ? (
                        <PipelineStepSummary
                          user={row.user}
                          awaitingFirstSpend={Boolean(row.hotel)}
                        />
                      ) : (
                        // Census sentence — a legacy row we only know from
                        // the tracking side.
                        <span className="text-sm text-content-secondary">
                          no onboarding pipeline recorded — campaigns have
                          never served
                        </span>
                      )}
                    </td>
                    <td className={TD_CLASS}>
                      <StepsProgress user={row.user} />
                    </td>
                    <td className={clsx(TD_CLASS, "whitespace-nowrap text-xs text-content-secondary")}>
                      {fromNow(row.user?.startedAt)}
                    </td>
                    <td className={clsx(TD_CLASS, "whitespace-nowrap text-xs text-content-secondary")}>
                      {fromNow(row.user?.lastUpdatedAt)}
                    </td>
                    <td className={clsx(TD_CLASS, "text-right whitespace-nowrap")}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          hiddenList.toggleHidden(row);
                        }}
                        data-tip={
                          isHidden
                            ? hiddenDetail(hiddenInfo)
                            : "Hide this row (stuck / churned) — for everyone, until someone unhides it"
                        }
                        className={clsx(
                          "text-xs font-medium transition-opacity",
                          isHidden
                            ? "text-brand-primary hover:text-brand-primary-hover"
                            : "text-gray-400 hover:text-content-primary opacity-0 group-hover:opacity-100",
                        )}
                      >
                        {isHidden ? "Unhide" : "Hide"}
                      </button>
                    </td>
                  </tr>
                );
              })}
        </tbody>
      </table>
    </div>
  );
}
